import {
    SceneNodes,
    TemplateNodes,
    TemplateGraph,
    onCompileTemplateInstanceImpl,
    TemplateInstance,
    EMPTY_LIST,
    EMPTY_OBJECT,
    isEvaluatedTemplateNode,
    setupInfoNode,
    GeometryGraphMesh,
    isMeshObjectData,
    getInterfaceIdPrefix,
    wrapInterfaceId,
    parseOutputTypeAndOutputIdFromOutputKey,
    TypeDescriptors,
    FilterAndWrapInterface,
    isCurveLikeNode,
} from "@cm/template-nodes"
import type {
    GraphBuilderScope,
    OnCompileContext,
    ObjectPathPart,
    TemplateNode,
    EvaluatedTemplateInputs,
    EvaluatedTemplateOutputs,
    BuilderOutlet,
    BuilderInlet,
    InterfaceDescriptor,
    CurveLikeNode,
    ObjectData,
} from "@cm/template-nodes"
import type {Mattress, MattressParameters} from "#mattress/nodes/mattress"
import type {MattressInfoNode} from "#mattress/nodes/mattress-info-node"
import {CompileRunMattressComponent} from "#mattress/runtime-graph/nodes/compile-run-mattress-component"
import {parseMattressOnContextOtherData, isLocalTemplateSubInstanceId} from "#mattress/nodes/component/mattress-component-helpers"
import {getWrappedComponentInstanceId} from "#mattress/nodes/component/mattress-component-instance"
import type {MattressComponentGraphData, MattressLocalNodesData, MattressMeshData} from "#mattress/nodes/component/mattress-component-types"
import {isMattressComponentInstanceLocalNodesData, isMattressCurveLocalNodesData} from "#mattress/nodes/component/mattress-component-types"
import {parseLocalNodesFromMattressMeshData, getLocalInstanceInputsDataFromMattressMeshData} from "#mattress/nodes/mattress-mesh"
import {MattressTemplateInterfaceDescriptorTypeDescriptor} from "#mattress/nodes/component/mattress-component-interface-descriptors"

import {IsNonNull} from "@cm/utils"

const TD = TypeDescriptors

const mattressTemplateInterfaceDescriptorArrayDesc = TD.Array(MattressTemplateInterfaceDescriptorTypeDescriptor)

function pullMeshSceneNodeFromOutput(output: unknown) {
    if (typeof output !== "object" || output === null) return null
    else if (!("type" in output)) return null
    const objectData = output as ObjectData
    if (!isMeshObjectData(objectData)) return null
    const meshSceneNodes = objectData.displayList.filter(SceneNodes.Mesh.is)
    for (const sceneNode of meshSceneNodes) {
        if (!sceneNode.path.some((node: unknown) => node instanceof GeometryGraphMesh)) continue
        return sceneNode
    }
    return null
}

type MattressLocalNode = TemplateNode

// TODO: workaround for the following issue, find a proper solution.
// Curve local nodes are produced via clone({cloneInstanceIds: true}), so each re-evaluation yields a new
// object under the same instanceId. The same curve is reachable through multiple independent reactive
// derivations (standalone local node and as a TemplateInstance parameter), and propagation is not atomic —
// so there is a transient window where two distinct objects might share one instanceId. Assembling those into the
// synthetic TemplateGraph would violate the engine's instanceId-uniqueness invariant (duplicate ids, colliding
// instanceId-keyed lookups, e.g. in node-evaluator caching logic). This guards against it: on collision we reject the whole snapshot
// and the caller holds the last consistent one until the dataflow re-converges.
function checkValidLocalNodes(localNodes: MattressLocalNode[]) {
    const localCurvesMap = new Map<string, CurveLikeNode>()
    const checkValid = (localCurve: CurveLikeNode) => {
        const seenLocalCurve = localCurvesMap.get(localCurve.instanceId)
        if (!seenLocalCurve) {
            localCurvesMap.set(localCurve.instanceId, localCurve)
            return true
        } else {
            return seenLocalCurve === localCurve
        }
    }

    for (const localNode of localNodes) {
        if (isCurveLikeNode(localNode) && !checkValid(localNode)) return false
        else if (
            localNode instanceof TemplateInstance &&
            Object.values(localNode.parameters.parameters.parameters).filter((x) => isCurveLikeNode(x) && !checkValid(x)).length !== 0
        )
            return false
    }
    return true
}

function evaluateMattressLocalTemplateInstanceData(
    scope: GraphBuilderScope,
    localNodesDataList: BuilderOutlet<MattressLocalNodesData[]>,
    mattressMeshData: BuilderOutlet<MattressMeshData | null>,
) {
    const meshLocalNodes = parseLocalNodesFromMattressMeshData(scope, mattressMeshData)

    const localNodesAndLocalInputsData = scope.pureLambda(
        localNodesDataList,
        (localNodesDataList) => {
            let localNodes: MattressLocalNode[] = []
            let localInputsData: EvaluatedTemplateInputs = {}
            const localSubTemplateInstanceNodePathString: {[localSubTemplateInstanceId: string]: string} = {}

            for (const localNodesData of localNodesDataList) {
                localNodes = [...localNodes, ...localNodesData.nodes]
                if (localNodesData.inputsData) localInputsData = {...localInputsData, ...localNodesData.inputsData}

                if (isMattressComponentInstanceLocalNodesData(localNodesData) && localNodesData.localSubInstance) {
                    const localSubInst = localNodesData.localSubInstance as TemplateNode & {parameters: {id: string}}
                    localSubTemplateInstanceNodePathString[localSubInst.parameters.id] = getWrappedComponentInstanceId(localNodesData.path)
                }
            }

            return {localNodes, localInputsData, localSubTemplateInstanceNodePathString}
        },
        "localTemplateInstanceData_localNodesAndLocalInputsData",
    )
    const otherLocalNodes = scope.get(localNodesAndLocalInputsData, "localNodes")
    const localInputsData = scope.get(localNodesAndLocalInputsData, "localInputsData")
    const localSubTemplateInstanceNodePathString = scope.get(localNodesAndLocalInputsData, "localSubTemplateInstanceNodePathString")

    const localNodes = scope.merge([meshLocalNodes, otherLocalNodes])
    const validLocalNodes = scope.branch(
        scope.pureLambda(
            localNodes,
            (localNodes) => {
                const valid = checkValidLocalNodes(localNodes)
                return valid ? localNodes : null
            },
            "checkValudLocalNodes",
        ),
    )[0]

    const localTemplateGraphSrc = scope.const(
        () =>
            new TemplateGraph({
                parameters: {
                    name: "",
                    nodes: new TemplateNodes({parameters: {list: []}}),
                },
            }),
        "localTemplateInstanceData_localTemplateGraphSrc",
    )

    const localTemplateGraph = scope.pureLambda(
        scope.tuple(localTemplateGraphSrc, validLocalNodes),
        ([localTemplateGraphSrc, localNodes]) => {
            localTemplateGraphSrc.parameters.nodes.clear()
            localNodes.forEach((localNode) => localTemplateGraphSrc.parameters.nodes.addEntry(localNode))
            localTemplateGraphSrc.parameters.nodes.parents.clear()
            return localTemplateGraphSrc.clone({
                cloneInstanceIds: true,
                cloneSubNode: () => false,
            })
        },
        "localTemplateInstanceData_localTemplateGraph",
    )

    const meshLocalInputsData = getLocalInstanceInputsDataFromMattressMeshData(scope, mattressMeshData)

    return scope.holdLast(
        scope.pureLambda(
            scope.tuple(localTemplateGraph, localInputsData, meshLocalInputsData, localSubTemplateInstanceNodePathString),
            ([localTemplateGraph, localInputsData, meshLocalInputsData, localSubTemplateInstanceNodePathString]) => {
                const inputsData: EvaluatedTemplateInputs = {
                    ...localInputsData,
                    ...meshLocalInputsData,
                }
                return {
                    templateData: {
                        graph: localTemplateGraph,
                    },
                    inputsData,
                    claimedInputIds: EMPTY_LIST,
                    subTemplateInstanceNodePathString: localSubTemplateInstanceNodePathString,
                }
            },
            "localTemplateInstanceData",
        ),
    )
}

export function mattressOnCompileImpl(context: OnCompileContext, mattress: Mattress, mattressParameters: MattressParameters) {
    const {evaluator, path, subTemplates, templateDepth} = context
    const {descriptorLists, liveValueAccessorLists} = subTemplates
    const {templateContext, activeNodeSet} = evaluator
    const {visible} = mattressParameters

    const mattressId = "mattress"

    const scope = evaluator.getScope(mattress)

    const rootComponentGraphData = evaluator.evaluate<MattressComponentGraphData | null>(mattress.parameters.root) as BuilderInlet<MattressComponentGraphData>
    const {focusedComponentInstancePath} = parseMattressOnContextOtherData(mattress, context)
    const rootLocalNodePath = EMPTY_LIST
    const newPath = scope.pureLambda(scope.tuple(path, mattress), ([path, instance]) => [...path, instance], "newPath", {
        output: TD.ObjectPath,
    })

    const [templateInputsData, claimedTemplateInputsIds] = evaluator.evaluateTemplateInputsForParameters(scope, EMPTY_OBJECT, mattressId)

    const forwardedLocalInstanceTemplateOutputs = scope.phi(
        scope.unresolvedToNull(scope.resolve<EvaluatedTemplateOutputs | null>(`mattressForwardedLocalInstanceTemplateOutputs`)),
        EMPTY_OBJECT,
    )
    const forwardedLocalInstanceDescriptorList = scope.phi(
        scope.branch(scope.unresolvedToNull(scope.resolve<InterfaceDescriptor<unknown>[] | null>(`mattressForwardedLocalInstanceDescriptorList`)))[0],
        EMPTY_LIST,
    )

    const {
        localNodesDataList,
        curvesDataList,
        referenceFrameDataList,
        cutsDataList,
        componentDescriptorList,
        templateDescriptorList,
        fullMattressMeshData,
        infoDataList,
    } = scope.node(CompileRunMattressComponent, {
        templateContext,
        graphData: rootComponentGraphData,
        templateInputs: templateInputsData,
        componentInputs: EMPTY_OBJECT,
        nodePath: rootLocalNodePath,
        objectPath: newPath,
        componentDepth: 0,
        partialMattressMeshData: null,
        exposeClaimedSubComponentInputs: templateDepth === 0,
        forwardedLocalInstanceTemplateOutputs,
        forwardedLocalInstanceDescriptorList,
    })

    const [localNodesDataListValid] = scope.branch(localNodesDataList)
    const [curvesDataListValid] = scope.branch(curvesDataList)
    const [referenceFrameDataListValid] = scope.branch(referenceFrameDataList)
    const [cutsDataListValid] = scope.branch(cutsDataList)

    const localTemplateInstanceData = evaluateMattressLocalTemplateInstanceData(scope, localNodesDataListValid, fullMattressMeshData)

    const localTemplateData = scope.get(localTemplateInstanceData, "templateData")
    const inputsData = scope.get(localTemplateInstanceData, "inputsData")
    const claimedInputIds = scope.get(localTemplateInstanceData, "claimedInputIds")
    const localSubTemplateInstanceNodePathString = scope.get(localTemplateInstanceData, "subTemplateInstanceNodePathString")

    const [transformMatrix, solverObject] = mattress.setupTransform(scope, context, undefined)
    const localId = evaluator.getLocalId(mattress)
    const computeDummyReifiedMeshData = templateContext.computeDummyReifiedMeshData ? !isEvaluatedTemplateNode(mattress, activeNodeSet) : false

    const localTemplateInstanceCompiledTemplateData = onCompileTemplateInstanceImpl(
        scope,
        context,
        localTemplateData,
        inputsData,
        claimedInputIds,
        transformMatrix,
        solverObject,
        newPath,
        visible,
        mattressId,
        localId,
        computeDummyReifiedMeshData,
        false,
        true,
    )

    const mattressCurveLiveValueAccessors = scope.pureLambda(
        scope.tuple(localTemplateInstanceCompiledTemplateData.liveValueAccessorList, localNodesDataListValid),
        ([subAccessors, localNodesDataList]) => {
            const cloneToMattressCurve = new Map<string, TemplateNode>()
            for (const localNodesData of localNodesDataList) {
                if (!isMattressCurveLocalNodesData(localNodesData)) continue
                const mattressCurve = localNodesData.path.at(-1)
                if (!mattressCurve) continue
                for (const node of localNodesData.nodes) cloneToMattressCurve.set(node.instanceId, mattressCurve)
            }
            const entries: typeof subAccessors = []
            for (const entry of subAccessors) {
                const mattressCurve = cloneToMattressCurve.get(entry.templateNode.instanceId)
                if (mattressCurve) entries.push({templateNode: mattressCurve, accessor: entry.accessor})
            }
            return entries
        },
        "mattressCurveLiveValueAccessors",
    )
    liveValueAccessorLists.push(mattressCurveLiveValueAccessors)

    const localTemplateInstanceSceneNodes = localTemplateInstanceCompiledTemplateData.displayList
    const localTemplateInstanceDescriptorList = scope.value(
        localTemplateInstanceCompiledTemplateData.descriptorList,
        mattressTemplateInterfaceDescriptorArrayDesc,
        "localTemplateInstanceDescriptorList",
    )
    const completeLocalTemplateInstanceOutputs = localTemplateInstanceCompiledTemplateData.outputs

    const localTemplateInstanceOutputsData = scope.pureLambda(
        scope.tuple(completeLocalTemplateInstanceOutputs, fullMattressMeshData),
        ([completeLocalTemplateInstanceOutputs, fullMattressMeshData]) => {
            const baseMeshSceneNodeExportId = fullMattressMeshData?.baseMeshLocalNodes?.export.parameters.id ?? null
            const segmentedMeshSceneNodeExportId = fullMattressMeshData?.segmentedMeshLocalNodes?.export.parameters.id ?? null

            let baseMeshSceneNode: SceneNodes.Mesh | null = null
            let segmentedMeshSceneNode: SceneNodes.Mesh | null = null
            const localTemplateInstanceOutputs: EvaluatedTemplateOutputs = {}

            for (const [key, output] of Object.entries(completeLocalTemplateInstanceOutputs)) {
                const {outputType, outputId} = parseOutputTypeAndOutputIdFromOutputKey(key)
                if (outputType === "Object") {
                    if (outputId === baseMeshSceneNodeExportId) baseMeshSceneNode = pullMeshSceneNodeFromOutput(output)
                    else if (outputId === segmentedMeshSceneNodeExportId) segmentedMeshSceneNode = pullMeshSceneNodeFromOutput(output)
                } else localTemplateInstanceOutputs[key] = output
            }

            return {
                localTemplateInstanceOutputs,
                baseMeshSceneNode,
                segmentedMeshSceneNode,
            }
        },
        "localTemplateInstanceOutputsData",
    )

    const filteredLocalTemplateInstanceDescriptorList = scope.pureLambda(
        scope.tuple(localTemplateInstanceDescriptorList, localSubTemplateInstanceNodePathString),
        ([descriptorList, subTemplateInstanceNodePathString]) =>
            descriptorList
                .map((d) => {
                    const [prefix, id] = getInterfaceIdPrefix(d.props.id)
                    if (prefix === null || !isLocalTemplateSubInstanceId(prefix)) return null
                    const nodePathString = subTemplateInstanceNodePathString[prefix]
                    if (!nodePathString) return null
                    return d.clone(wrapInterfaceId(nodePathString, id))
                })
                .filter(IsNonNull),
        "filteredLocalTemplateInstanceDescriptorList",
        {
            output: mattressTemplateInterfaceDescriptorArrayDesc,
        },
    )

    scope.alias(scope.holdLast(scope.get(localTemplateInstanceOutputsData, "localTemplateInstanceOutputs")), `mattressForwardedLocalInstanceTemplateOutputs`)
    scope.alias(scope.holdLast(filteredLocalTemplateInstanceDescriptorList), `mattressForwardedLocalInstanceDescriptorList`)

    const focusedComponentInstanceWrappedId = scope.pureLambda(
        focusedComponentInstancePath,
        (focusedComponentInstancePath) => (focusedComponentInstancePath === "root" ? null : getWrappedComponentInstanceId(focusedComponentInstancePath)),
        "focusedComponentInstanceWrappedId",
    )

    const extendedTemplateDescriptorList = scope.pureLambda(
        scope.tuple(scope.phi(scope.branch(templateDescriptorList)[0], EMPTY_LIST), localTemplateInstanceDescriptorList, fullMattressMeshData),
        ([templateDescs, localDescs, meshData]) => {
            const localIdToTemplateDescId = new Map<string, string>()

            if (meshData?.geometryParameterSourceNodes) {
                for (const [localId, sourceNode] of Object.entries(meshData.geometryParameterSourceNodes)) {
                    const matchingDesc = templateDescs.find((d) => d.props.affectedObjectPathParts?.some((p: ObjectPathPart) => p.path.includes(sourceNode)))
                    if (matchingDesc) localIdToTemplateDescId.set(localId, matchingDesc.props.id)
                }
            }

            if (meshData?.segmentedMeshMaterialAssignments && meshData?.segmentedMeshMaterialSlotsOwners) {
                for (const [materialIdStr, assignmentData] of Object.entries(meshData.segmentedMeshMaterialAssignments)) {
                    if (!assignmentData?.sourceNode) continue
                    const materialId = parseInt(materialIdStr)
                    const ownerPath = meshData.segmentedMeshMaterialSlotsOwners[materialId]
                    const {localMaterialInput, sourceNode} = assignmentData
                    const rawId = "id" in sourceNode.parameters ? (sourceNode.parameters.id as string) : null
                    if (!rawId) continue
                    const ownerWrappedId = ownerPath ? getWrappedComponentInstanceId(ownerPath) : ""
                    const wrappedId = ownerWrappedId ? wrapInterfaceId(ownerWrappedId, rawId) : rawId
                    localIdToTemplateDescId.set(localMaterialInput.parameters.id, wrappedId)
                }
            }

            return templateDescs.map((desc) => {
                if (desc.props.type !== "input") return desc
                const localDesc = localDescs.find((d) => d.props.id === desc.props.id || localIdToTemplateDescId.get(d.props.id) === desc.props.id)
                if (!localDesc?.props.affectedObjectPathParts) return desc
                const extraPathParts: ObjectPathPart[] = localDesc.props.affectedObjectPathParts
                if (extraPathParts.length === 0) return desc
                const cloned = desc.clone(desc.props.id, desc.props.isSetByTemplate)
                cloned.props.affectedObjectPathParts = [...(desc.props.affectedObjectPathParts ?? []), ...extraPathParts]
                return cloned
            })
        },
        "extendedTemplateDescriptorList",
    )

    descriptorLists.push(
        scope.node(FilterAndWrapInterface, {
            interface: extendedTemplateDescriptorList,
            wrapWithId: mattressId,
            claimedInputIds: claimedTemplateInputsIds,
            includeClaimed: true,
        }).output,
    )

    setupInfoNode(mattress, scope, context, "Mattress", (props) =>
        scope.struct<MattressInfoNode>("Mattress", {
            type: "Mattress",
            ...props,
            localNodesDataList: localNodesDataListValid,
            curvesDataList: curvesDataListValid,
            referenceFramesDataList: referenceFrameDataListValid,
            cutsDataList: cutsDataListValid,
            focusedComponentInstancePath,
            focusedComponentInstanceWrappedId,
            meshGeometryParameters: scope.branch(scope.get(fullMattressMeshData, "geometryParameters"))[0],
            baseMeshSceneNode: scope.phi(scope.get(localTemplateInstanceOutputsData, "baseMeshSceneNode"), null),
            segmentedMeshSceneNode: scope.phi(scope.get(localTemplateInstanceOutputsData, "segmentedMeshSceneNode"), null),
            segmentedMeshMaterialSlotsOwners: scope.phi(scope.branch(scope.get(fullMattressMeshData, "segmentedMeshMaterialSlotsOwners"))[0], null),
            localSceneNodes: scope.phi(localTemplateInstanceSceneNodes, null),
            templateDescriptorList: scope.phi(templateDescriptorList, null),
            componentDescriptorList: scope.phi(componentDescriptorList, null),
            infoDataList: scope.phi(infoDataList, null),
        }),
    )
}
